import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface DangerousGoodsDeclarationDialogProps {
  data: any
  shipmentId: string
  documentId: string
  onSave: (data: any, changes?: any[]) => void
}

const DangerousGoodsDeclarationDialog: React.FC<DangerousGoodsDeclarationDialogProps> = ({
  data,
  shipmentId,
  documentId,
  onSave,
}) => {
  // Define sections for Dangerous Goods Declaration
  const sections: DocumentSection[] = [
    {
      id: 'documentInfo',
      title: 'Document Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Document Number', path: 'documentNumber' },
        { type: 'field', label: 'Reference Number', path: 'referenceNumber' },
        { type: 'field', label: 'Issue Date', path: 'issueDate' },
        { type: 'field', label: 'Shipment Date', path: 'shipmentDate' },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Shipper',
          path: 'shipper',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
            { label: 'Emergency Contact', path: 'emergencyContact' },
          ],
        },
        {
          type: 'object',
          label: 'Consignee',
          path: 'consignee',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
          ],
        },
        {
          type: 'object',
          label: 'Carrier',
          path: 'carrier',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Contact', path: 'contact' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'transportInfo',
      title: 'Transport Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Transport Mode', path: 'transportMode' },
        {
          type: 'object',
          label: 'Vessel Information',
          path: 'vessel',
          fields: [
            { label: 'Vessel Name', path: 'name' },
            { label: 'Voyage', path: 'voyage' },
          ],
          optional: true,
        },
        {
          type: 'object',
          label: 'Aircraft Information',
          path: 'aircraft',
          fields: [
            { label: 'Flight Number', path: 'flightNumber' },
            { label: 'Date', path: 'date' },
          ],
          optional: true,
        },
        {
          type: 'object',
          label: 'Vehicle Information',
          path: 'vehicle',
          fields: [
            { label: 'Registration Number', path: 'registrationNumber' },
            { label: 'Date', path: 'date' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'locations',
      title: 'Locations',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Port of Loading', path: 'portOfLoading' },
        { type: 'field', label: 'Port of Discharge', path: 'portOfDischarge' },
        { type: 'field', label: 'Place of Origin', path: 'placeOfOrigin' },
        { type: 'field', label: 'Final Destination', path: 'finalDestination' },
      ],
    },
    {
      id: 'dangerousGoods',
      title: 'Dangerous Goods',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Dangerous Goods',
          path: 'dangerousGoods',
          columns: [
            { label: 'UN Number', path: 'unNumber' },
            { label: 'Proper Shipping Name', path: 'properShippingName' },
            { label: 'Class', path: 'class' },
            { label: 'Packing Group', path: 'packingGroup' },
            { label: 'Number of Packages', path: 'numberOfPackages' },
            { label: 'Type of Packaging', path: 'typeOfPackaging' },
            {
              label: 'Quantity',
              path: 'quantity',
              complexType: 'weight',
            },
            { label: 'Marine Pollutant', path: 'marinepollutant' },
            {
              label: 'Flash Point',
              path: 'flashPoint',
              complexType: 'weight',
            },
            { label: 'Technical Name', path: 'technicalName' },
            { label: 'Emergency Response Code', path: 'emergencyResponseCode' },
          ],
        },
      ],
    },
    {
      id: 'containerInfo',
      title: 'Container Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Container Number', path: 'containerNumber' },
        { type: 'field', label: 'Seal Number', path: 'sealNumber' },
        { type: 'field', label: 'Container Packed By', path: 'containerPackedBy' },
        { type: 'field', label: 'Vehicle Size', path: 'vehicleSize' },
        { type: 'field', label: 'Packing Date', path: 'packingDate' },
      ],
    },
    {
      id: 'additionalInfo',
      title: 'Additional Information',
      layout: 'single',
      fields: [
        {
          type: 'field',
          label: 'Additional Handling Information',
          path: 'additionalHandlingInformation',
          fullWidth: true,
        },
        { type: 'field', label: 'Special Instructions', path: 'specialInstructions', fullWidth: true },
      ],
    },
    {
      id: 'declarations',
      title: 'Declarations and Signatures',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Shipper Declaration', path: 'shipperDeclaration', fullWidth: true },
        { type: 'field', label: 'Carrier Approval', path: 'carrierApproval', fullWidth: true },
        { type: 'field', label: 'Shipper Signature', path: 'shipperSignature' },
        { type: 'field', label: 'Carrier Signature', path: 'carrierSignature' },
        { type: 'field', label: 'Date of Signature', path: 'dateOfSignature' },
      ],
    },
  ]

  return (
    <DocumentDialog
      documentData={data}
      schema={{}}
      sections={sections}
      documentType="Dangerous Goods Declaration"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default DangerousGoodsDeclarationDialog
